import { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

/** ✅ UX knobs — tweak these only */
const WORD_LANE = {
  heightDesktop: 34,
  heightMobile: 30,
  gapFromPlot: 8,
  fontDesktop: 14,
  fontMobile: 13,
  padXDesktop: 14,
  padXMobile: 12,
  minChipDesktop: 34,
  minChipMobile: 28,
  radius: 999,
};

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/**
 * ✅ Lane plugin:
 * - reserves room INSIDE chart area for lane
 * - draws words as chips in lane
 * - clips chips to xWindow [min,max] (voiced window from PitchAnimation)
 * - draws cursor through plot + lane
 */
function makeLanePlugin(getLanePx) {
  return {
    id: "lanePlugin",

    beforeLayout(chart, _args, opts) {
      const lanePx = getLanePx(!!opts?.isMobile);
      const x = chart.scales?.x;
      if (!x) return;

      x.options.afterFit = (scale) => {
        scale.height = (scale.height || 0) + lanePx;
      };
    },

    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        wordIntervals = [],
        activeWord = null,
        cursorX = null,
        showWordLabels = true,
        isMobile = false,
        xWindowMin = null,
        xWindowMax = null,
      } = pluginOptions || {};

      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      if (!ctx || !chartArea || !xScale) return;

      const { top, bottom, left, right } = chartArea;

      const laneH = isMobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop;
      const gap = WORD_LANE.gapFromPlot;
      const laneTop = bottom - laneH;
      const laneBottom = bottom;

      const fontSize = isMobile ? WORD_LANE.fontMobile : WORD_LANE.fontDesktop;
      const padX = isMobile ? WORD_LANE.padXMobile : WORD_LANE.padXDesktop;
      const minChipW = isMobile ? WORD_LANE.minChipMobile : WORD_LANE.minChipDesktop;

      const clipMin =
        Number.isFinite(Number(xWindowMin)) ? Number(xWindowMin) : null;
      const clipMax =
        Number.isFinite(Number(xWindowMax)) ? Number(xWindowMax) : null;

      ctx.save();

      // Clip to chart area
      ctx.beginPath();
      ctx.rect(left, top, right - left, bottom - top);
      ctx.clip();

      // Lane background
      ctx.fillStyle = "rgba(15,23,42,0.035)";
      ctx.strokeStyle = "rgba(148,163,184,0.30)";
      ctx.lineWidth = 1;
      roundRect(ctx, left, laneTop, right - left, laneH, 12);
      ctx.fill();
      ctx.stroke();

      // Divider line
      ctx.strokeStyle = "rgba(148,163,184,0.20)";
      ctx.beginPath();
      ctx.moveTo(left, laneTop - gap + 0.5);
      ctx.lineTo(right, laneTop - gap + 0.5);
      ctx.stroke();

      const chipY = laneTop + 3;
      const chipH = laneH - 6;

      ctx.font = `600 ${fontSize}px system-ui, -apple-system, Segoe UI, Roboto, Arial`;
      ctx.textBaseline = "middle";
      ctx.textAlign = "center";

      for (const w of wordIntervals) {
        const startC = clipMin == null ? w.start : Math.max(w.start, clipMin);
        const endC = clipMax == null ? w.end : Math.min(w.end, clipMax);
        if (!(endC > startC)) continue;

        const x1 = xScale.getPixelForValue(startC);
        const x2 = xScale.getPixelForValue(endC);

        const segLeft = Math.min(x1, x2);
        const segW = Math.max(1, Math.abs(x2 - x1));
        const segCenter = segLeft + segW / 2;

        const drawW = Math.max(minChipW, segW);
        let chipX = segLeft;
        if (segW < minChipW) chipX = segCenter - drawW / 2;

        const inset = 2;
        chipX = clamp(chipX, left + inset, right - inset - drawW);

        const isActive =
          activeWord &&
          w.start === activeWord.start &&
          w.end === activeWord.end;

        if (isActive) {
          ctx.fillStyle = "rgba(16,185,129,0.22)";
          ctx.strokeStyle = "rgba(16,185,129,0.95)";
          ctx.lineWidth = 1.4;

          ctx.shadowColor = "rgba(0,0,0,0.10)";
          ctx.shadowBlur = 6;
          ctx.shadowOffsetY = 2;

          roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
          ctx.stroke();
        } else {
          ctx.fillStyle = "rgba(241,245,249,0.70)";
          ctx.strokeStyle = "rgba(148,163,184,0.55)";
          ctx.lineWidth = 1;

          roundRect(ctx, chipX, chipY, drawW, chipH, WORD_LANE.radius);
          ctx.fill();
          ctx.stroke();
        }

        if (showWordLabels) {
          const available = drawW - padX * 2;
          let text = w.text ?? "";

          if (ctx.measureText(text).width > available) {
            while (text.length > 1 && ctx.measureText(text + "…").width > available) {
              text = text.slice(0, -1);
            }
            if (text !== (w.text ?? "")) text += "…";
          }

          ctx.fillStyle = isActive
            ? "rgba(5,150,105,1)"
            : "rgba(51,65,85,0.90)";
          ctx.fillText(text, chipX + drawW / 2, chipY + chipH / 2);
        }
      }

      // Cursor
      if (cursorX != null) {
        const xC = xScale.getPixelForValue(cursorX);
        if (xC >= left && xC <= right) {
          ctx.strokeStyle = "rgba(255,255,255,0.90)";
          ctx.lineWidth = isMobile ? 3 : 3.5;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, laneBottom);
          ctx.stroke();

          ctx.strokeStyle = "rgba(16,185,129,1)";
          ctx.lineWidth = isMobile ? 1.6 : 1.8;
          ctx.beginPath();
          ctx.moveTo(xC, top);
          ctx.lineTo(xC, laneBottom);
          ctx.stroke();
        }
      }

      ctx.restore();
    },
  };
}

export default function IntensityAnimation({
  intensity,
  segment,
  broadcastTime,
  showIntensity = true,
  /** pass these from parent (PitchAnimation) if you want PERFECT alignment */
  voicedXMin: voicedXMinProp,
  voicedXMax: voicedXMaxProp,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [showWordLabels, setShowWordLabels] = useState(true);

  const xs = useMemo(() => intensity?.xs || [], [intensity]);
  const ys = useMemo(() => intensity?.ys || [], [intensity]);

  const wordIntervals = useMemo(() => {
    const segStart = Number(segment?.start ?? 0);
    return (
      segment?.words?.map((w) => ({
        start: Number(w.start) - segStart,
        end: Number(w.end) - segStart,
        text: String(w.text ?? ""),
      })) || []
    );
  }, [segment]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setShowWordLabels(!mobile && wordIntervals.length <= 18);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [wordIntervals.length]);

  // No early returns before hooks
  const hasData = xs.length > 0 && ys.length > 0;

  // If parent doesn't provide voiced window, use full intensity range.
  const voicedXMin = useMemo(() => {
    if (Number.isFinite(Number(voicedXMinProp))) return Number(voicedXMinProp);
    return xs.length ? xs[0] : 0;
  }, [voicedXMinProp, xs]);

  const voicedXMax = useMemo(() => {
    if (Number.isFinite(Number(voicedXMaxProp))) return Number(voicedXMaxProp);
    return xs.length ? xs[xs.length - 1] : 1;
  }, [voicedXMaxProp, xs]);

  // cursor “waits” before window start, “sticks” after window end
  const cursorX = useMemo(() => {
    if (!xs.length) return null;
    const t = Number(broadcastTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;
    return clamp(raw, voicedXMin, voicedXMax);
  }, [broadcastTime, xs.length, voicedXMin, voicedXMax]);

  const activeWord = useMemo(() => {
    if (cursorX == null) return null;
    return wordIntervals.find((w) => cursorX >= w.start && cursorX <= w.end) || null;
  }, [cursorX, wordIntervals]);

  const shouldShowWordLabels = useMemo(() => {
    return showWordLabels && wordIntervals.length > 0 && wordIntervals.length <= 18;
  }, [showWordLabels, wordIntervals.length]);

  const { yMin, yMax } = useMemo(() => {
    if (!ys.length) return { yMin: 0, yMax: 1 };
    return { yMin: Math.min(...ys), yMax: Math.max(...ys) };
  }, [ys]);

  const yRange = useMemo(() => Math.max(1e-6, yMax - yMin), [yMax, yMin]);

  const lanePxGetter = useMemo(() => {
    return (mobile) =>
      (mobile ? WORD_LANE.heightMobile : WORD_LANE.heightDesktop) +
      WORD_LANE.gapFromPlot;
  }, []);
  const lanePlugin = useMemo(() => makeLanePlugin(lanePxGetter), [lanePxGetter]);

  const data = useMemo(() => {
    const datasets = [];

    if (showIntensity) {
      datasets.push({
        label: "Intensity (dB)",
        data: xs.map((x, i) => ({ x, y: ys[i] })),
        borderColor: "#2563eb",
        borderWidth: isMobile ? 1.75 : 2.25,
        tension: isMobile ? 0.25 : 0.42,
        cubicInterpolationMode: "monotone",
        pointRadius: 0,
        showLine: true,
        datalabels: { display: false },
      });
    }

    // ✅ removed red maxima line + dots entirely

    return { datasets };
  }, [xs, ys, showIntensity, isMobile]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 8 } },

      scales: {
        x: {
          type: "linear",
          min: voicedXMin, // ✅ same voiced window as pitch
          max: voicedXMax,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          ticks: { display: false }, // ✅ no x labels
          title: { display: false }, // ✅ no x title
        },
        y: {
          min: yMin - yRange * 0.08,
          max: yMax + yRange * 0.08,
          grid: { drawBorder: false, color: "rgba(148,163,184,0.14)" },
          title: {
            display: true,
            text: "Intensity (dB)",
            color: "rgba(51,65,85,0.80)",
            font: { size: isMobile ? 12 : 13, weight: "600" },
          },
          ticks: {
            color: "rgba(51,65,85,0.70)",
            font: { size: isMobile ? 10 : 11 },
            maxTicksLimit: 5,
          },
        },
      },

      plugins: {
        legend: { display: false },
        tooltip: { enabled: !isMobile, mode: "nearest", intersect: false },
        datalabels: { display: false },

        lanePlugin: {
          wordIntervals,
          activeWord,
          cursorX,
          showWordLabels: shouldShowWordLabels,
          isMobile,
          xWindowMin: voicedXMin, // ✅ used to clip words to voiced window
          xWindowMax: voicedXMax,
        },
      },

      interaction: { mode: "nearest", axis: "x", intersect: false },
    };
  }, [
    isMobile,
    yMin,
    yMax,
    yRange,
    wordIntervals,
    activeWord,
    cursorX,
    shouldShowWordLabels,
    voicedXMin,
    voicedXMax,
  ]);

  // Render decision after hooks
  if (!hasData) return null;

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      {isMobile && wordIntervals.length > 0 && (
        <label className="flex items-center gap-2 select-none text-xs">
          <input
            type="checkbox"
            checked={showWordLabels}
            onChange={() => setShowWordLabels((v) => !v)}
            className="scale-90"
          />
          Show word labels
        </label>
      )}

      <div
        className={`w-full min-w-0 overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 ${
          isMobile ? "h-48" : "h-64"
        }`}
      >
        <Line data={data} options={options} plugins={[lanePlugin]} />
      </div>

      {isMobile && (
        <p className="text-xs text-gray-500 text-center">
          Pinch to zoom • Word labels: {showWordLabels ? "ON" : "OFF"}
        </p>
      )}
    </div>
  );
}